import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL;
export const API = BASE ? `${BASE}/api` : null;

export const fetchProducts = async () => {
  if (!API) return [];
  try {
    const res = await axios.get(`${API}/products`);
    return Array.isArray(res.data) ? res.data : [];
  } catch {
    return [];
  }
};

export const createOrder = async (items, customer, method) => (await axios.post(`${API}/orders`, {
  items: items.map((i) => ({ product_id: i.id, quantity: i.qty })), customer, method,
}, { withCredentials: true })).data;

export const redsysReturn = async (fields) => (await axios.post(`${API}/redsys/return`, fields)).data;
export const getTransferInfo = async (ref) => (await axios.get(`${API}/orders/${ref}/transfer`)).data;

// Sends the signed fields to Redsys with a real form POST (Redsys hosts the card / Bizum screen).
export const submitToRedsys = (action, fields) => {
  const form = document.createElement("form");
  form.method = "POST";
  form.action = action;
  Object.entries(fields).forEach(([name, value]) => {
    const input = document.createElement("input");
    input.type = "hidden"; input.name = name; input.value = String(value);
    form.appendChild(input);
  });
  document.body.appendChild(form);
  form.submit();
};

const admin = axios.create({ withCredentials: true });
export const adminApi = {
  session: async (sessionId) => (await admin.post(`${API}/auth/session`, { session_id: sessionId })).data,
  me: async () => (await admin.get(`${API}/auth/me`)).data,
  logout: async () => (await admin.post(`${API}/auth/logout`)).data,
  orders: async (status, customerId) => (await admin.get(`${API}/admin/orders`, { params: { status, customer_id: customerId || undefined } })).data,
  customers: async (q) => (await admin.get(`${API}/admin/customers`, { params: { q } })).data,
  customersCsv: async (q) => (await admin.get(`${API}/admin/customers.csv`, { params: { q }, responseType: "blob" })).data,
  markPaid: async (ref) => (await admin.post(`${API}/admin/orders/${ref}/mark-paid`)).data,
  ship: async (ref, tracking) => (await admin.post(`${API}/admin/orders/${ref}/ship`, tracking)).data,
};

export const getPaymentStatus = async (sid) => (await axios.get(`${API}/payments/status/${sid}`)).data;

export const getSessionId = () => {
  let id = localStorage.getItem("jn_chat_sid");
  if (!id) {
    id = `jn-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem("jn_chat_sid", id);
  }
  return id;
};

export async function streamChat(sessionId, message, onToken) {
  return streamSSE("/chat", { session_id: sessionId, message }, onToken);
}

export const imageToJpeg = (file, max = 1280) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const s = Math.min(1, max / Math.max(img.width, img.height));
      const c = document.createElement("canvas");
      c.width = Math.round(img.width * s);
      c.height = Math.round(img.height * s);
      c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
      URL.revokeObjectURL(url);
      resolve(c.toDataURL("image/jpeg", 0.85));
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("bad image")); };
    img.src = url;
  });

export async function streamSSE(path, payload, onToken) {
  const res = await fetch(`${API}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok || !res.body) throw new Error("chat failed");
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    const parts = buf.split("\n\n");
    buf = parts.pop();
    for (const p of parts) {
      const d = p.replace(/^data: /, "");
      if (d === "[DONE]") return;
      try {
        const j = JSON.parse(d);
        if (j.error) throw new Error(j.error);
        if (j.t) onToken(j.t);
      } catch (e) {
        if (e.message && !e.message.includes("JSON")) throw e;
      }
    }
  }
}

export const eur = (n) =>
  new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(Number(n) || 0);

export const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -70, duration: 1.6 });
  else el.scrollIntoView({ behavior: "smooth" });
};

// For deep links on page load: keeps re-aligning while images/sections above finish loading.
export const settleOnId = (id, tries = 8) => {
  let timer;
  let stopped = false;
  const stop = () => { stopped = true; };
  window.addEventListener("touchstart", stop, { once: true, passive: true });
  window.addEventListener("wheel", stop, { once: true, passive: true });
  const step = (left) => {
    if (stopped) return;
    const el = document.getElementById(id);
    if (el && Math.abs(el.getBoundingClientRect().top - 86) > 24) {
      if (window.__lenis) window.__lenis.scrollTo(el, { offset: -86, immediate: true, force: true });
      else window.scrollTo({ top: window.scrollY + el.getBoundingClientRect().top - 86 });
    }
    if (left > 0) timer = setTimeout(() => step(left - 1), 500);
  };
  timer = setTimeout(() => step(tries), 400);
  return () => {
    clearTimeout(timer);
    window.removeEventListener("touchstart", stop);
    window.removeEventListener("wheel", stop);
  };
};
